import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardHeader, CardContent, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, Chip, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  MenuItem, Select, FormControl, InputLabel, Alert, Snackbar,
  Paper, Grid, Tooltip, Badge, CircularProgress, styled
} from '@mui/material';
import {
  Plus, Edit2, Trash2, Power, Wifi, WifiOff, Search, CircuitBoard,
  Settings, RefreshCw, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../api';
import ConfirmModal from '../ConfirmModal';

// Custom styled switch for better visibility
const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4caf50',
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.5,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
  },
}));

// PLC Tipleri Yapılandırması
const PLC_TYPES = [
  {
    value: 'SIEMENS',
    label: 'Siemens S7',
    icon: '🏭',
    description: 'CP343.dll Protokolü',
    defaultPort: 102,
    requiresSlot: true
  },
  {
    value: 'MODBUS',
    label: 'MODBUS TCP',
    icon: '📡',
    description: 'Standart MODBUS Protokolü',
    defaultPort: 502,
    requiresSlot: false
  },
  {
    value: 'MITSUBISHI',
    label: 'Mitsubishi MELSEC',
    icon: '🔧',
    description: 'MC Protokolü (3E/1E Çerçeve)',
    defaultPort: 5007,
    requiresSlot: false
  },
  {
    value: 'OPC',
    label: 'OPC Server',
    icon: '⚙️',
    description: 'OPC Classic/UA Protokolü',
    defaultPort: 135,
    requiresSlot: false
  },
  {
    value: 'BACNET',
    label: 'BACnet',
    icon: '🏢',
    description: 'Bina Otomasyon Protokolü',
    defaultPort: 47808,
    requiresSlot: false
  }
];

const CONNECTION_STATUS = {
  CONNECTED: { color: 'success', label: 'Bağlı', icon: CheckCircle },
  DISCONNECTED: { color: 'default', label: 'Bağlı Değil', icon: XCircle },
  CONNECTING: { color: 'warning', label: 'Bağlanıyor...', icon: CircularProgress },
  ERROR: { color: 'error', label: 'Bağlantı Hatası', icon: AlertCircle }
};

export default function PLCManagement() {
  const [plcs, setPlcs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlc, setSelectedPlc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [connectionStates, setConnectionStates] = useState({});
  const [testingConnection, setTestingConnection] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletePlcId, setDeletePlcId] = useState(null);

  const [formData, setFormData] = useState({
    plcName: '',
    plcAddress: '',
    slotNo: 2,
    isActive: true,
    plcType: 'SIEMENS',
    port: 102
  });

  useEffect(() => {
    loadPlcs();
  }, []);

  // Map numeric PLC type to string
  const getPlcTypeString = (numericType) => {
    const typeMap = {
      1: 'SIEMENS',
      2: 'MODBUS', 
      3: 'MITSUBISHI',
      4: 'OPC',
      5: 'BACNET'
    };
    return typeMap[numericType] || 'SIEMENS';
  };

  const loadPlcs = async () => {
    setLoading(true);
    try {
      const plcsData = await api.dataSources.getAllPlcs();
      console.log('[loadPlcs] Raw API response:', plcsData);
      // Map database field names to frontend field names
      const mappedPlcs = (plcsData || []).map(plc => ({
        plcId: plc.plcID || plc.PlcID || plc.plcId || plc.PlcId,
        plcName: plc.PlcName || plc.plcName,
        plcAddress: plc.PlcAddress || plc.plcAddress,
        slotNo: plc.SlotNo || plc.slotNo,
        plcType: getPlcTypeString(plc.PlcType || plc.plcType),
        isActive: plc.IsActive !== undefined ? plc.IsActive : plc.isActive,
        connectionStatus: plc.ConnectionStatus || plc.connectionStatus || 'DISCONNECTED',
        lastUpdate: plc.LastUpdate || plc.lastUpdate
      }));
      console.log('[loadPlcs] Mapped PLCs:', mappedPlcs);
      setPlcs(mappedPlcs);
      // Initialize connection states
      const states = {};
      mappedPlcs.forEach(plc => {
        states[plc.plcId] = plc.connectionStatus || 'DISCONNECTED';
      });
      setConnectionStates(states);
    } catch (error) {
      console.error('Error loading PLCs:', error);
      toast.error('PLC listesi yüklenemedi: ' + (error.message || 'Bilinmeyen hata'));
      setPlcs([]);
      setConnectionStates({});
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleConnectPLC = async (plcId) => {
    setConnectionStates(prev => ({ ...prev, [plcId]: 'CONNECTING' }));
    
    try {
      const response = await fetch(`/api/DataSource/plc/${plcId}/connect`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setConnectionStates(prev => ({ ...prev, [plcId]: 'CONNECTED' }));
        showSnackbar('PLC başarıyla bağlandı', 'success');
      } else {
        setConnectionStates(prev => ({ ...prev, [plcId]: 'ERROR' }));
        showSnackbar(data.error || 'PLC bağlantısı başarısız', 'error');
      }
    } catch (error) {
      setConnectionStates(prev => ({ ...prev, [plcId]: 'ERROR' }));
      console.error('Error connecting to PLC:', error);
      showSnackbar('PLC bağlantı hatası: ' + error.message, 'error');
    }
  };

  const handleDisconnectPLC = async (plcId) => {
    try {
      const response = await fetch(`/api/DataSource/plc/${plcId}/disconnect`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setConnectionStates(prev => ({ ...prev, [plcId]: 'DISCONNECTED' }));
        showSnackbar('PLC bağlantısı kesildi', 'success');
      } else {
        showSnackbar(data.error || 'PLC bağlantısı kesilemedi', 'error');
      }
    } catch (error) {
      console.error('Error disconnecting from PLC:', error);
      showSnackbar('PLC bağlantı kesme hatası: ' + error.message, 'error');
    }
  };

  const handleTestConnection = async (plcId) => {
    setTestingConnection(plcId);
    
    try {
      const response = await fetch(`/api/DataSource/plc/${plcId}/status`);
      const data = await response.json();
      
      if (response.ok) {
        const status = data.isConnected ? 'CONNECTED' : 'DISCONNECTED';
        setConnectionStates(prev => ({ ...prev, [plcId]: status }));
        const statusText = data.isConnected ? 'Bağlı' : 'Bağlı Değil';
        showSnackbar(`Bağlantı testi tamamlandı - ${statusText}`, 'info');
      } else {
        showSnackbar('Bağlantı testi başarısız', 'error');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      showSnackbar('Bağlantı test hatası: ' + error.message, 'error');
    } finally {
      setTestingConnection(null);
    }
  };

  const getPLCTypeInfo = (plcType) => {
    return PLC_TYPES.find(type => type.value === plcType) || PLC_TYPES[0];
  };

  const handleOpenDialog = (plc = null) => {
    if (plc) {
      setSelectedPlc(plc);
      const typeInfo = getPLCTypeInfo(plc.plcType);
      setFormData({
        plcName: plc.plcName,
        plcAddress: plc.plcAddress,
        slotNo: plc.slotNo || (typeInfo.requiresSlot ? 2 : 0),
        isActive: plc.isActive,
        plcType: plc.plcType,
        port: plc.port || typeInfo.defaultPort
      });
    } else {
      const defaultType = PLC_TYPES[0];
      setSelectedPlc(null);
      setFormData({
        plcName: '',
        plcAddress: '',
        slotNo: defaultType.requiresSlot ? 2 : 0,
        isActive: true,
        plcType: defaultType.value,
        port: defaultType.defaultPort
      });
    }
    setDialogOpen(true);
  };

  const handlePLCTypeChange = (newType) => {
    const typeInfo = getPLCTypeInfo(newType);
    setFormData(prev => ({
      ...prev,
      plcType: newType,
      port: typeInfo.defaultPort,
      slotNo: typeInfo.requiresSlot ? (prev.slotNo || 2) : 0
    }));
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPlc(null);
    const defaultType = PLC_TYPES[0];
    setFormData({
      plcName: '',
      plcAddress: '',
      slotNo: defaultType.requiresSlot ? 2 : 0,
      isActive: true,
      plcType: defaultType.value,
      port: defaultType.defaultPort
    });
  };

  const handleSave = async () => {
    try {
      if (selectedPlc) {
        // Update existing PLC
        await api.dataSources.updatePlc({ ...selectedPlc, ...formData });
        toast.success('PLC başarıyla güncellendi');
      } else {
        // Create new PLC
        await api.dataSources.createPlc(formData);
        toast.success('PLC başarıyla eklendi');
      }
      handleCloseDialog();
      await loadPlcs(); // Refresh the list to get updated data from backend
    } catch (error) {
      console.error('Error saving PLC:', error);
      toast.error('PLC kaydedilirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
      // Don't close dialog on error so user can retry or fix data
    }
  };

  const handleDelete = (plcId) => {
    console.log('[handleDelete] plcId:', plcId);
    setDeletePlcId(plcId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    console.log('[confirmDelete] deletePlcId state:', deletePlcId);
    try {
      await api.dataSources.deletePlc(deletePlcId);
      toast.success('PLC başarıyla silindi');
      setConfirmOpen(false);
      await loadPlcs(); // Refresh the list to get updated data from backend
    } catch (error) {
      console.error('Error deleting PLC:', error);
      toast.error('PLC silinirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
      setConfirmOpen(false);
    }
  };

  const handleToggleStatus = async (plcId) => {
    try {
      setPlcs(plcs.map(p => 
        p.plcId === plcId 
          ? { ...p, isActive: !p.isActive, connectionStatus: !p.isActive ? 'connecting' : 'disconnected' }
          : p
      ));
      toast.success('PLC durumu güncellendi');
    } catch (error) {
      console.error('Error toggling PLC status:', error);
      toast.error('PLC durumu güncellenirken hata oluştu');
    }
  };

  const handleDeviceDiscovery = async () => {
    setDiscovering(true);
    try {
      toast.info('Cihaz keşfi başlatılıyor...');
      // Simulate device discovery - in production this would call backend API
      await new Promise(resolve => setTimeout(resolve, 2000));
      await loadPlcs();
      toast.success('Cihaz keşfi tamamlandı');
    } catch (error) {
      console.error('Device discovery error:', error);
      toast.error('Cihaz keşfi hatası: ' + error.message);
    } finally {
      setDiscovering(false);
    }
  };

  const renderConnectionStatus = (plcId, plc) => {
    if (!plc.isActive) {
      return <Chip label="Pasif" size="small" color="default" />;
    }
    
    let status = connectionStates[plcId] || plc.connectionStatus || 'DISCONNECTED';
    
    // Normalize status values from database to match CONNECTION_STATUS keys
    if (status === 'Online') status = 'CONNECTED';
    if (status === 'Offline') status = 'DISCONNECTED';
    
    const statusInfo = CONNECTION_STATUS[status] || CONNECTION_STATUS['DISCONNECTED'];
    const IconComponent = statusInfo.icon;
    
    return (
      <Chip
        icon={<IconComponent size={14} />}
        label={statusInfo.label}
        color={statusInfo.color}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">PLC Cihazları</Typography>
                <Typography variant="body2" color="textSecondary">
                  Siemens, MODBUS, Mitsubishi, OPC ve BACnet PLC'leri yönetin
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={`${plcs.length} PLC`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  startIcon={<RefreshCw size={16} />}
                  onClick={loadPlcs}
                  disabled={loading}
                >
                  Yenile
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Search size={16} />}
                  onClick={handleDeviceDiscovery}
                  disabled={discovering}
                  sx={{ mr: 1 }}
                >
                  {discovering ? 'Keşfediliyor...' : 'Keşfet'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={() => handleOpenDialog()}
                  disabled={loading}
                  sx={{
                    bgcolor: '#edbc45',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#d4a63a'
                    }
                  }}
                >
                  PLC Ekle
                </Button>
              </Box>
            </Box>
          }
        />
        
        <CardContent sx={{ flex: 1, pt: 1, overflow: 'auto' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Ad</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Adres</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Son Güncelleme</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plcs.map((plc) => {
                  const typeInfo = getPLCTypeInfo(plc.plcType);
                  return (
                    <TableRow key={plc.plcId} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px', fontSize: '20px' }}>
                            {typeInfo.icon}
                          </span>
                          <Box>
                            <Typography variant="subtitle2">
                              {plc.plcName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {plc.plcId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{typeInfo.label}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {typeInfo.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontFamily="monospace">
                            {plc.plcAddress}
                          </Typography>
                          {typeInfo.requiresSlot && plc.slotNo && (
                            <Typography variant="caption" color="textSecondary">
                              Slot: {plc.slotNo}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{renderConnectionStatus(plc.plcId, plc)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {plc.lastUpdate ?
                            new Date(plc.lastUpdate).toLocaleString('tr-TR') :
                            'Hiç'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {connectionStates[plc.plcId] === 'CONNECTED' ? (
                            <Tooltip title="Bağlantıyı Kes">
                              <IconButton
                                size="small"
                                onClick={() => handleDisconnectPLC(plc.plcId)}
                                color="error"
                              >
                                <Power size={16} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Bağlan">
                              <IconButton
                                size="small"
                                onClick={() => handleConnectPLC(plc.plcId)}
                                color="success"
                              >
                                <Power size={16} />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="Bağlantıyı Test Et">
                            <IconButton
                              size="small"
                              onClick={() => handleTestConnection(plc.plcId)}
                              disabled={testingConnection === plc.plcId}
                            >
                              {testingConnection === plc.plcId ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Settings size={16} />
                              )}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(plc)}
                              color="primary"
                            >
                              <Edit2 size={16} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(plc.plcId)}
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Durumu Yenile">
                            <IconButton
                              size="small"
                              onClick={() => handleTestConnection(plc.plcId)}
                            >
                              <RefreshCw size={16} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {plcs.length === 0 && !loading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 300,
              color: 'text.secondary',
              py: 4
            }}>
              <CircuitBoard size={64} style={{ marginBottom: 24, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Yapılandırılmış PLC cihazı yok</Typography>
              <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', maxWidth: '400px' }}>
                Endüstriyel bağlantıları yönetmeye başlamak için ilk PLC'nizi ekleyin.
                Siemens S7, MODBUS TCP, Mitsubishi MELSEC, OPC ve BACnet protokollerini destekliyoruz.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => handleOpenDialog()}
                sx={{
                  bgcolor: '#edbc45',
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#d4a63a'
                  }
                }}
              >
                İlk PLC'nizi Ekleyin
              </Button>
            </Box>
          )}

          {loading && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 200 
            }}>
              <CircularProgress />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* PLC Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedPlc ? 'PLC Cihazını Düzenle' : 'PLC Cihazı Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="PLC Adı"
              value={formData.plcName}
              onChange={(e) => setFormData({ ...formData, plcName: e.target.value })}
              fullWidth
              required
              placeholder="örn. Ana Üretim Hattı PLC"
            />

            <FormControl fullWidth required>
              <InputLabel>PLC Tipi</InputLabel>
              <Select
                value={formData.plcType}
                label="PLC Tipi"
                onChange={(e) => handlePLCTypeChange(e.target.value)}
              >
                {PLC_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{type.icon}</span>
                      <Box>
                        <Typography variant="body2">{type.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="IP Adresi"
              value={formData.plcAddress}
              onChange={(e) => setFormData({ ...formData, plcAddress: e.target.value })}
              fullWidth
              required
              placeholder="192.168.1.100"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Port"
                type="number"
                value={formData.port}
                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 0 })}
                fullWidth
                helperText={`Varsayılan: ${getPLCTypeInfo(formData.plcType).defaultPort}`}
              />

              {getPLCTypeInfo(formData.plcType).requiresSlot && (
                <TextField
                  label="Slot Numarası"
                  type="number"
                  value={formData.slotNo}
                  onChange={(e) => setFormData({ ...formData, slotNo: parseInt(e.target.value) || 2 })}
                  fullWidth
                  helperText="Siemens PLC slotu (varsayılan: 2)"
                />
              )}
            </Box>

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="caption">
                <strong>{getPLCTypeInfo(formData.plcType).label}</strong>: {getPLCTypeInfo(formData.plcType).description}
                {getPLCTypeInfo(formData.plcType).requiresSlot && ' - Siemens PLC\'ler için slot yapılandırması gerekir'}
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <IOSSwitch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Aktif"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.plcName || !formData.plcAddress}
          >
            {selectedPlc ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="PLC'yi Sil"
        content="Bu PLC'yi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        type="yesNo"
      />
    </Box>
  );
}